import Marquee from "react-fast-marquee";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Nav from "@/components/Nav";
import { motion } from "framer-motion";
import { ShieldCheck, Zap, Eye, Database, ArrowRight, Terminal } from "lucide-react";

const BROKERS = [
  "ACXIOM", "SPOKEO", "WHITEPAGES", "BEENVERIFIED", "MYLIFE", "RADARIS",
  "INTELIUS", "PEEKYOU", "PIPL", "NUWBER", "TRUEPEOPLESEARCH", "PEOPLEFINDER",
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="grain min-h-screen bg-[#050505] text-white">
      <Nav />

      {/* HERO */}
      <section className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 md:grid-cols-12 md:py-32">
          <div className="col-span-1 md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6 inline-flex items-center gap-2 border border-[#E5FF00]/40 bg-[#E5FF00]/5 px-3 py-1"
            >
              <span className="h-1.5 w-1.5 animate-pulse bg-[#E5FF00]"></span>
              <span data-testid="hero-status" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5FF00]">Scanning 400+ brokers · Live</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-5xl font-medium leading-[0.95] tracking-tighter md:text-7xl"
            >
              Reclaim your identity <br/>
              from the <span className="text-[#FF3333]">dark.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8 max-w-xl text-lg leading-relaxed text-zinc-400"
            >
              OBSIDIAN is an AI wizard that hunts your personal data across the dark web and 400+ people-search brokers — then obliterates every trace. Weaponized privacy, for a fee.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Button data-testid="hero-cta-scan" onClick={() => nav("/signup")}
                className="btn-cta group h-12 rounded-none border border-[#E5FF00] bg-[#E5FF00] px-6 font-mono text-[11px] uppercase tracking-[0.25em] text-black hover:bg-transparent hover:text-[#E5FF00]">
                Start Dark Web Scan <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button data-testid="hero-cta-pricing" variant="outline" onClick={() => nav("/pricing")}
                className="btn-cta h-12 rounded-none border-white/20 bg-transparent px-6 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300 hover:border-white hover:text-white">
                See Pricing
              </Button>
            </motion.div>
            <div className="mt-10 flex items-center gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              <span>[SSL / E2E]</span><span>[GDPR + CCPA]</span><span>[15,000+ removals]</span>
            </div>
          </div>

          {/* Terminal mock */}
          <div className="col-span-1 md:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="relative overflow-hidden border border-white/10 bg-[#0A0A0C] scanline"
            >
              <div className="flex items-center justify-between border-b border-white/10 bg-black/60 px-4 py-2">
                <div className="flex items-center gap-2">
                  <Terminal className="h-3 w-3 text-[#E5FF00]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">obsidian@dark ~ scan.sh</span>
                </div>
                <div className="flex gap-1"><span className="h-2 w-2 bg-zinc-700"></span><span className="h-2 w-2 bg-zinc-700"></span><span className="h-2 w-2 bg-[#FF3333]"></span></div>
              </div>
              <pre className="whitespace-pre-wrap p-5 font-mono text-[11px] leading-relaxed text-zinc-300">
{`> initializing OBSIDIAN v1.4.2
> auth: [OK] target authenticated
> scanning 412 brokers ...
[FOUND]   whitepages.com     :: name, phone, address
[FOUND]   spokeo.com         :: relatives, age, employer
[FOUND]   beenverified.com   :: SSN partial, email
[FOUND]   radaris.com        :: photos, DOB
[FOUND]   acxiom.com         :: purchase history
[WARN]    exposure_score: 87/99 CRITICAL
> generating removal plan …`}
                <span className="caret"></span>
              </pre>
            </motion.div>
          </div>
        </div>

        {/* broker marquee */}
        <div className="border-y border-white/10 bg-black/40 py-4">
          <Marquee gradient={false} speed={40} pauseOnHover>
            {BROKERS.concat(BROKERS).map((b, i) => (
              <span key={i} className="mx-10 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600">
                <span className="text-[#FF3333]">◆</span> {b}
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Feature grid */}
      <section className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-0 border-l border-white/10 md:grid-cols-3">
          {[
            { icon: Eye, title: "AI Deep-Scan", desc: "Claude Sonnet 4.5 scans data-broker exposure and hostile leak-sites in seconds." },
            { icon: Zap, title: "Automated Removal", desc: "Programmatic opt-out requests + evidence tracking for every broker discovered." },
            { icon: ShieldCheck, title: "Continuous Monitoring", desc: "New leak? Re-listed? OBSIDIAN files the takedown before you know it happened." },
          ].map((f, i) => (
            <div key={i} className="group border-b border-r border-white/10 p-10 transition-colors hover:bg-white/[0.02]">
              <f.icon className="h-6 w-6 text-[#E5FF00]" strokeWidth={1.5} />
              <h3 className="mt-8 font-display text-2xl font-medium tracking-tight">{f.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-zinc-400">{f.desc}</p>
              <div className="mt-6 h-px w-8 bg-[#E5FF00] transition-all duration-300 group-hover:w-16"></div>
            </div>
          ))}
        </div>
      </section>

      {/* stats band */}
      <section className="border-b border-white/10 bg-black/40">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-0 md:grid-cols-4">
          {[
            { n: "412", l: "Brokers Scanned" },
            { n: "15.2K", l: "Records Removed" },
            { n: "94%", l: "Success Rate" },
            { n: "24h", l: "First-Response" },
          ].map((s, i) => (
            <div key={i} className="border-r border-white/10 p-10">
              <div className="font-display text-4xl font-medium tracking-tight md:text-5xl">{s.n}</div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 py-24 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-display text-4xl font-medium tracking-tight md:text-5xl">
              Your data is already <span className="text-[#FF3333]">out there.</span>
            </h2>
            <p className="mt-4 max-w-xl text-zinc-400">Run your first scan in under 90 seconds. No credit card to see your exposure score.</p>
          </div>
          <Button data-testid="cta-bottom-scan" onClick={() => nav("/signup")}
            className="btn-cta h-14 rounded-none border border-[#E5FF00] bg-[#E5FF00] px-8 font-mono text-xs uppercase tracking-[0.25em] text-black hover:bg-transparent hover:text-[#E5FF00]">
            Start Free Scan <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">© 2026 OBSIDIAN LABS · ENCRYPTED · SECURE</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">Powered by Claude Sonnet 4.5</span>
        </div>
      </footer>
    </div>
  );
}
